<template>
  <div class="topology">
    <section v-if="fronts.length" class="tier front">
      <header class="tier-head">
        <h2 class="tier-title">Front</h2>
        <p class="tier-text">L'application et les sites, servis aux navigateurs</p>
        <span class="tier-count" :class="{bad: !allUp(fronts)}">{{ upText(fronts) }}</span>
      </header>
      <div class="cards">
        <ContainerCard v-for="container in fronts" :key="container.id" :container="container" :thresholds="thresholds" />
      </div>
    </section>

    <div v-if="fronts.length && gateways.length" class="link">
      <span class="line"></span>
      <div class="link-label">
        <strong>Appels de l'API et visites</strong>
        <span class="link-text">Les fronts ne parlent qu'à la passerelle, jamais directement aux services</span>
      </div>
      <span class="line"></span>
      <span class="arrow"></span>
    </div>

    <section v-if="gateways.length" class="tier gateway">
      <header class="tier-head">
        <h2 class="tier-title">Passerelle</h2>
        <p class="tier-text">Point d'entrée de l'API : routage, limitation de débit, supervision et audience</p>
        <span class="tier-count" :class="{bad: !allUp(gateways)}">{{ upText(gateways) }}</span>
      </header>
      <div class="cards single-centered">
        <ContainerCard v-for="container in gateways" :key="container.id" :container="container" :thresholds="thresholds" />
      </div>
    </section>

    <div v-if="proxied.length || internals.length" class="layer links" :class="{alone: !internals.length}">
      <div v-if="proxied.length" class="link">
        <span class="line"></span>
        <div class="link-label">
          <strong>Routes de la passerelle</strong>
          <span class="routes"><code v-for="route in routes" :key="route">{{ route }}</code></span>
        </div>
        <span class="line"></span>
        <span class="arrow"></span>
      </div>
      <div v-if="internals.length" class="link dashed">
        <span class="line"></span>
        <div class="link-label">
          <strong>Supervision seulement</strong>
          <span class="link-text">Aucune route : la passerelle ne fait que les sonder</span>
        </div>
        <span class="line"></span>
        <span class="arrow"></span>
      </div>
    </div>

    <div v-if="proxied.length || internals.length" class="layer" :class="{alone: !internals.length}">
      <section v-if="proxied.length" class="tier back">
        <header class="tier-head">
          <h2 class="tier-title">Back</h2>
          <p class="tier-text">Les services de l'application, joints par une route de la passerelle</p>
          <span class="tier-count" :class="{bad: !allUp(proxied)}">{{ upText(proxied) }}</span>
        </header>
        <div class="cards">
          <ContainerCard v-for="container in proxied" :key="container.id" :container="container" :thresholds="thresholds" />
        </div>
      </section>
      <section v-if="internals.length" class="tier internal">
        <header class="tier-head">
          <h2 class="tier-title">
            <svg class="lock" viewBox="0 0 16 16" width="13" height="13" aria-hidden="true"><path fill="currentColor" d="M8 1a3.5 3.5 0 0 0-3.5 3.5V6H4a1 1 0 0 0-1 1v6a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1h-.5V4.5A3.5 3.5 0 0 0 8 1Zm-2 3.5a2 2 0 1 1 4 0V6H6V4.5Z"/></svg>
            Back interne
          </h2>
          <p class="tier-text">Non exposés : seuls les autres containers les appellent</p>
          <span class="tier-count" :class="{bad: !allUp(internals)}">{{ upText(internals) }}</span>
        </header>
        <div class="cards">
          <ContainerCard v-for="container in internals" :key="container.id" :container="container" :thresholds="thresholds" />
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import ContainerCard from "@/administration/components/ContainerCard.vue";

/**
 * Les containers en trois niveaux, de haut en bas : les fronts, la passerelle, puis le back (les services joints par une route de la passerelle,
 * et les services internes qu'aucune route n'atteint). Les liens entre les niveaux sont dessinés : appels des fronts, routes de la passerelle,
 * simple supervision pour les containers internes.
 * containers : les containers renvoyés par la passerelle ; thresholds : seuils d'alerte.
 */
export default {
  name: 'ContainerTopology',
  components: {ContainerCard},
  props: {
    containers: {type: Array, required: true},
    thresholds: {type: Object, required: true},
  },
  computed: {
    fronts() {
      return this.containers.filter(container => container.tier === 'front');
    },
    gateways() {
      return this.containers.filter(container => container.tier === 'gateway');
    },
    // Back joint par une route de la passerelle
    proxied() {
      return this.containers.filter(container => container.tier === 'back' && container.exposure === 'gateway');
    },
    // Back qu'aucune route n'atteint
    internals() {
      return this.containers.filter(container => container.tier === 'back' && container.exposure === 'internal');
    },
    routes() {
      return this.proxied.map(container => `/${container.route}`);
    },
  },
  methods: {
    allUp(containers) {
      return containers.every(container => container.status === 'up');
    },
    upText(containers) {
      return `${containers.filter(container => container.status === 'up').length} / ${containers.length} en ligne`;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

LINK_COLOR = THEME_COLOR_PRIMARY
DASHED_COLOR = #9aa5bd

// Un niveau : une zone teintée, un titre, les encadrés des containers
.tier
  min-width 0
  padding 14px 16px 16px
  border 1px solid CTRL_BORDER
  border-radius 16px

.tier-head
  display flex
  flex-wrap wrap
  align-items baseline
  gap 2px 12px
  margin-bottom 12px

.tier-title
  display flex
  align-items center
  gap 6px
  margin 0
  font-size 12.5px
  font-weight 700
  letter-spacing .08em
  text-transform uppercase

.tier-text
  flex 1 1 220px
  margin 0
  font-size 12.5px
  color CTRL_TEXT_MUTED

.tier-count
  font-size 12px
  font-weight 600
  color #2f8f4e

  &.bad
    color CTRL_ERROR

.cards
  display grid
  grid-template-columns repeat(auto-fill, minmax(190px, 1fr))
  gap 8px

  // La passerelle seule, au centre
  &.single-centered
    grid-template-columns minmax(0, 1fr)
    max-width 380px
    margin 0 auto

.tier.front
  background #f2fbfb
  border-color #bfe6e6

  .tier-title
    color #1f8a8a

.tier.gateway
  background #eef1f9
  border 2px solid THEME_COLOR_PRIMARY

  .tier-title
    color THEME_COLOR_PRIMARY

.tier.back
  background #f7f8fc
  border-color #d5dbea

  .tier-title
    color THEME_COLOR_PRIMARY_FADDED

.tier.internal
  background #f6f6f7
  border 1px dashed DASHED_COLOR

  .tier-title
    color #5b6477

// Deux colonnes alignées : les services joints par une route, et les services internes
.layer
  display grid
  grid-template-columns minmax(0, 2fr) minmax(0, 1fr)
  gap 16px

  &.alone
    grid-template-columns minmax(0, 1fr)

  @media only screen and (max-width: DESKTOP_SIZE)
    grid-template-columns minmax(0, 1fr)

// Un lien entre deux niveaux : un trait, une étiquette, un trait et une flèche vers le niveau suivant
.link
  display flex
  flex-direction column
  align-items center
  min-width 0

.line
  width 0
  height 14px
  border-left 2px solid LINK_COLOR

.arrow
  width 0
  height 0
  border-left 6px solid transparent
  border-right 6px solid transparent
  border-top 8px solid LINK_COLOR

.link-label
  display flex
  flex-direction column
  align-items center
  gap 3px
  max-width 100%
  padding 6px 16px
  text-align center
  color LINK_COLOR
  background white
  border 1px solid LINK_COLOR
  border-radius 12px

  strong
    font-size 13px

.link-text
  font-size 12px
  color CTRL_TEXT_MUTED

.routes
  display flex
  flex-wrap wrap
  justify-content center
  gap 4px 6px

  code
    padding 0 7px
    font-size 12px
    font-weight 600
    color LINK_COLOR
    background #eef1f9
    border-radius 6px

.link.dashed
  .line
    border-left-style dashed
    border-left-color DASHED_COLOR

  .arrow
    border-top-color DASHED_COLOR

  .link-label
    color #5b6477
    border-style dashed
    border-color DASHED_COLOR
</style>
